import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { collectionReceiptAttachmentsApi } from '#/api/crmMgmt/customerOrder/collection';
import { ATTACHMENT_POLICY_STANDARD } from '#/api/platform/attachments';
import { PERM } from '#/config/permissions';
import { DocumentAttachmentsCard } from '#/modules/commonComponents/attachments';
import { Button } from '#/modules/commonComponents/button';
import { AppDialog } from '#/modules/commonComponents/dialog';
import {
  COLLECTION_RECEIPT_STATUS_LABEL,
  canRecordCollection,
  canReverseCollection,
  mapReceiptRow,
} from '#/modules/crmMgmt/customerOrder/helper/collection';
import { useCan } from '#/modules/infra/auth/useCan';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayAmount } from '#/utils/formatNumber';

import CollectionReceiptConfirmDialog from './CollectionReceiptConfirmDialog';
import CollectionReceiptDraftDialog from './CollectionReceiptDraftDialog';
import CollectionReceiptVoidDialog from './CollectionReceiptVoidDialog';
import CollectionSimpleTable from './CollectionSimpleTable';
import CollectionStatusBadge from './CollectionStatusBadge';

function formatCredits(credits = [], phaseNoById) {
  if (credits.length === 0) {
    return '—';
  }
  return credits
    .map((credit) => `第${phaseNoById.get(credit.phaseId) ?? '?'}期 ${formatDisplayAmount(credit.amount)}`)
    .join('、');
}

const CollectionReceiptsCard = ({ detail, receipts, summary }) => {
  const customerOrderId = detail.id;
  const canUpdate = useCan(PERM.FMS_CUSTOMER_COLLECTION_UPDATE);
  const canConfirm = useCan(PERM.FMS_CUSTOMER_COLLECTION_CONFIRM);
  // { type: 'create'|'edit'|'confirm'|'void'|'attachments', receipt? }
  const [dialog, setDialog] = useState(null);
  const summaryPhases = useMemo(() => summary?.phases ?? [], [summary]);
  const recordable = canRecordCollection(detail.status);
  const reversible = canReverseCollection(detail.status);

  const rows = useMemo(() => receipts.map(mapReceiptRow), [receipts]);
  const columns = useMemo(() => {
    const phaseNoById = new Map(summaryPhases.map((phase) => [phase.phaseId, phase.phaseNo]));
    return [
      {
        id: 'status',
        header: '狀態',
        accessorKey: 'status',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => (
          <CollectionStatusBadge status={row.original.status} labels={COLLECTION_RECEIPT_STATUS_LABEL} />
        ),
        meta: { statusPresentation: 'badge' },
      },
      {
        id: 'amount',
        header: '金額',
        accessorFn: (row) => formatDisplayAmount(row.amount),
        size: 110,
        enableSorting: false,
        meta: { cellClassName: 'td_right' },
      },
      {
        id: 'actualReceivedAt',
        header: '實收時間',
        accessorFn: (row) => formatDateTimeOrDash(row.actualReceivedAt),
        size: 150,
        enableSorting: false,
      },
      {
        id: 'credits',
        header: '抵付期別',
        accessorFn: (row) => formatCredits(row.credits, phaseNoById),
        size: 180,
        enableSorting: false,
      },
      {
        id: 'remark',
        header: '備註',
        accessorFn: (row) => row.remark || '—',
        size: 160,
        enableSorting: false,
        meta: { cellClassName: 'td_left' },
      },
      {
        id: 'actions',
        header: '操作',
        size: 220,
        enableSorting: false,
        cell: ({ row }) => {
          const receipt = row.original;
          const isDraft = receipt.status === 'DRAFT';
          const isConfirmed = receipt.status === 'CONFIRMED';
          return (
            <div className="flex flex-wrap gap-1">
              {isDraft && canUpdate && recordable ? (
                <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'edit', receipt })}>
                  修改
                </Button>
              ) : null}
              {isDraft && canConfirm && recordable ? (
                <Button size="sm" onClick={() => setDialog({ type: 'confirm', receipt })}>
                  確認
                </Button>
              ) : null}
              <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'attachments', receipt })}>
                憑證
              </Button>
              {isConfirmed && canConfirm && reversible ? (
                <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'void', receipt })}>
                  沖銷
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ];
  }, [summaryPhases, canUpdate, canConfirm, recordable, reversible]);

  const closeDialog = (nextOpen) => {
    if (!nextOpen) {
      setDialog(null);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-4 pt-3">
        {detail.status === 'CLOSED' ? (
          <span className="text-sm text-slate-500">已結案客訂需先「重新開啟」才能沖銷或退款</span>
        ) : null}
        {canUpdate && recordable ? (
          <Button type="button" size="sm" className="ml-auto" onClick={() => setDialog({ type: 'create' })}>
            登錄收款
          </Button>
        ) : null}
      </div>
      <CollectionSimpleTable
        rows={rows}
        columns={columns}
        emptyText="尚無收款紀錄"
        rowIdPrefix="receipt"
      />

      {dialog?.type === 'create' || dialog?.type === 'edit' ? (
        <CollectionReceiptDraftDialog
          open
          onOpenChange={closeDialog}
          customerOrderId={customerOrderId}
          receipt={dialog.receipt}
          summaryPhases={summaryPhases}
          canConfirm={canConfirm}
        />
      ) : null}
      {dialog?.type === 'confirm' ? (
        <CollectionReceiptConfirmDialog
          open
          onOpenChange={closeDialog}
          customerOrderId={customerOrderId}
          receipt={dialog.receipt}
          summaryPhases={summaryPhases}
        />
      ) : null}
      {dialog?.type === 'void' ? (
        <CollectionReceiptVoidDialog
          open
          onOpenChange={closeDialog}
          customerOrderId={customerOrderId}
          receipt={dialog.receipt}
        />
      ) : null}
      {dialog?.type === 'attachments' ? (
        <AppDialog
          open
          onOpenChange={closeDialog}
          title="收款憑證"
          description="僅財務可見；憑證為選填，不能取代實收確認。"
          headerVariant="muted"
          maxWidthClassName="sm:max-w-2xl"
          showCancel={false}
        >
          <DocumentAttachmentsCard
            businessId={String(dialog.receipt.id)}
            ownerKey="customerCollectionReceipt"
            api={collectionReceiptAttachmentsApi}
            policy={ATTACHMENT_POLICY_STANDARD}
            readOnly={!canUpdate}
            variant="section"
          />
        </AppDialog>
      ) : null}
    </div>
  );
};

CollectionReceiptsCard.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string,
  }).isRequired,
  receipts: PropTypes.arrayOf(PropTypes.object).isRequired,
  summary: PropTypes.object,
};

export default CollectionReceiptsCard;
